import { useMemo, useState } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { allAdminBookings, AdminBookingFull, AvailableCleaner } from "@/mocks/admin";
import AssignCleanerModal from "./components/AssignCleanerModal";
import BookingDrawer from "./components/BookingDrawer";
import StatusMenu from "./components/StatusMenu";

type Status = AdminBookingFull["status"];

const statusFilters: { id: "all" | Status; label: string; tone: string }[] = [
  { id: "all", label: "All", tone: "text-foreground-800" },
  { id: "pending", label: "Needs cleaner", tone: "text-accent-800" },
  { id: "scheduled", label: "Scheduled", tone: "text-secondary-800" },
  { id: "in_progress", label: "In progress", tone: "text-primary-800" },
  { id: "completed", label: "Completed", tone: "text-foreground-800" },
  { id: "cancelled", label: "Cancelled", tone: "text-foreground-600" },
];

const dateFilters = [
  { id: "all", label: "Any date" },
  { id: "today", label: "Today" },
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
];

const serviceFilters = [
  "All services",
  "Standard Clean",
  "Deep Clean",
  "Move-in Clean",
  "Move-out Clean",
  "Airbnb Turnover",
];

const cityFilters = [
  "All cities",
  "Brooklyn",
  "Park Slope",
  "Chelsea",
  "Williamsburg",
  "Brooklyn Heights",
  "Kensington",
  "Upper East Side",
  "SoHo",
  "Morningside Heights",
  "Carroll Gardens",
  "East Village",
  "Prospect Heights",
];

const paymentTint: Record<string, string> = {
  paid: "bg-primary-100 text-primary-800",
  due: "bg-accent-100 text-accent-800",
  refunded: "bg-background-200 text-foreground-700",
};

const today = "2026-06-17";

export default function AdminBookingsPage() {
  const [bookings, setBookings] = useState<AdminBookingFull[]>(allAdminBookings);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | Status>("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("All services");
  const [cityFilter, setCityFilter] = useState("All cities");
  const [showFilters, setShowFilters] = useState(false);

  const [assignTarget, setAssignTarget] = useState<AdminBookingFull | null>(null);
  const [drawerTarget, setDrawerTarget] = useState<AdminBookingFull | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: bookings.length };
    for (const b of bookings) map[b.status] = (map[b.status] || 0) + 1;
    return map;
  }, [bookings]);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (dateFilter === "today" && b.date !== today) return false;
      if (dateFilter === "upcoming" && b.date <= today) return false;
      if (dateFilter === "past" && b.date >= today) return false;
      if (serviceFilter !== "All services" && b.service !== serviceFilter) return false;
      if (cityFilter !== "All cities" && b.city !== cityFilter) return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        const hit =
          b.id.toLowerCase().includes(q) ||
          b.customer.toLowerCase().includes(q) ||
          (b.cleanerName || "").toLowerCase().includes(q) ||
          b.address.toLowerCase().includes(q) ||
          b.city.toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [bookings, statusFilter, dateFilter, serviceFilter, cityFilter, query]);

  const flashToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const updateStatus = (id: string, next: Status) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, status: next } : b))
    );
    setDrawerTarget((d) => (d && d.id === id ? { ...d, status: next } : d));
    flashToast(`${id} marked as ${next.replace("_", " ")}`);
  };

  const assignCleaner = (cleaner: AvailableCleaner) => {
    if (!assignTarget) return;
    const id = assignTarget.id;
    setBookings((prev) =>
      prev.map((b) =>
        b.id === id
          ? {
              ...b,
              cleanerId: cleaner.id,
              cleanerName: cleaner.name,
              cleanerAvatar: cleaner.image,
              status: b.status === "pending" ? "scheduled" : b.status,
            }
          : b
      )
    );
    setDrawerTarget((d) =>
      d && d.id === id
        ? {
            ...d,
            cleanerId: cleaner.id,
            cleanerName: cleaner.name,
            cleanerAvatar: cleaner.image,
            status: d.status === "pending" ? "scheduled" : d.status,
          }
        : d
    );
    setAssignTarget(null);
    flashToast(`${cleaner.name} assigned to ${id}`);
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setDateFilter("all");
    setServiceFilter("All services");
    setCityFilter("All cities");
    setQuery("");
  };

  const activeFilters =
    (statusFilter !== "all" ? 1 : 0) +
    (dateFilter !== "all" ? 1 : 0) +
    (serviceFilter !== "All services" ? 1 : 0) +
    (cityFilter !== "All cities" ? 1 : 0);

  return (
    <AdminLayout
      title="Bookings"
      subtitle={`${bookings.length} total · ${counts.pending || 0} need a cleaner · ${counts.in_progress || 0} in progress`}
      action={
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button className="hidden sm:inline-flex items-center gap-1.5 bg-background-50 hover:bg-background-200 border border-background-200 text-foreground-800 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
            <i className="ri-download-line"></i> Export
          </button>
          <button className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 pl-4 pr-2 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
            New booking
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50">
              <i className="ri-add-line text-primary-600 text-sm"></i>
            </span>
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Status tabs */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-1.5 flex items-center gap-1 overflow-x-auto">
          {statusFilters.map((s) => {
            const active = statusFilter === s.id;
            const count = counts[s.id] ?? 0;
            return (
              <button
                key={s.id}
                onClick={() => setStatusFilter(s.id)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs md:text-sm font-medium whitespace-nowrap cursor-pointer transition ${
                  active
                    ? "bg-foreground-950 text-background-50"
                    : `text-foreground-700 hover:text-foreground-950 hover:bg-background-100`
                }`}
              >
                {s.label}
                <span
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                    active ? "bg-background-50/15" : "bg-background-100"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search + filter row */}
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search booking #, customer, cleaner, address…"
              className="w-full bg-background-50 border border-background-200 placeholder:text-foreground-500 text-sm text-foreground-900 rounded-full pl-9 pr-3 py-2.5 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters((v) => !v)}
              className="md:hidden flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium bg-background-50 border border-background-200 text-foreground-800 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-equalizer-3-line"></i> Filters
              {activeFilters > 0 && (
                <span className="text-[10px] bg-primary-500 text-background-50 rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilters}
                </span>
              )}
            </button>
            {activeFilters > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap cursor-pointer px-2"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Filter chips */}
        <div className={`${showFilters ? "grid" : "hidden md:grid"} grid-cols-1 sm:grid-cols-3 gap-2`}>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-foreground-600 mb-1 block">
              Date
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full bg-background-50 border border-background-200 text-sm rounded-lg px-3 py-2 outline-none focus:border-primary-300 cursor-pointer pr-8"
            >
              {dateFilters.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-foreground-600 mb-1 block">
              Service
            </label>
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="w-full bg-background-50 border border-background-200 text-sm rounded-lg px-3 py-2 outline-none focus:border-primary-300 cursor-pointer pr-8"
            >
              {serviceFilters.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-foreground-600 mb-1 block">
              City
            </label>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="w-full bg-background-50 border border-background-200 text-sm rounded-lg px-3 py-2 outline-none focus:border-primary-300 cursor-pointer pr-8"
            >
              {cityFilters.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between text-xs text-foreground-600">
          <span>
            Showing <span className="font-semibold text-foreground-900">{filtered.length}</span> of {bookings.length}
          </span>
          <div className="hidden sm:flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary-500"></span> In progress
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span> Needs cleaner
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span> Scheduled
            </span>
          </div>
        </div>

        {/* Table — desktop */}
        <div className="hidden md:block bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-foreground-500 bg-background-100/60">
                  <th className="px-5 py-3 font-medium">Booking</th>
                  <th className="px-3 py-3 font-medium">Customer</th>
                  <th className="px-3 py-3 font-medium">Service</th>
                  <th className="px-3 py-3 font-medium">When</th>
                  <th className="px-3 py-3 font-medium">Cleaner</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-3 py-3 font-medium text-right">Amount</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => setDrawerTarget(b)}
                    className="border-t border-background-200 hover:bg-background-100/60 transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-3">
                      <p className="font-semibold text-foreground-950">{b.id}</p>
                      <p className="text-[11px] text-foreground-500">{b.createdAt}</p>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2.5">
                        <img
                          src={b.customerAvatar}
                          alt={b.customer}
                          className="w-8 h-8 rounded-full object-cover object-top"
                        />
                        <div className="min-w-0">
                          <p className="text-foreground-950 truncate">{b.customer}</p>
                          <p className="text-[11px] text-foreground-500 truncate">
                            {b.address} · {b.city}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-foreground-800 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <i className={`${b.serviceIcon} text-primary-600`}></i>
                        {b.service}
                      </div>
                      <p className="text-[11px] text-foreground-500 mt-0.5">
                        {b.bedrooms} bd · {b.bathrooms} ba · {b.duration}
                      </p>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <p className="text-foreground-900 font-medium">{b.dateLabel}</p>
                      <p className="text-[11px] text-foreground-500">{b.time}</p>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {b.cleanerId && b.cleanerAvatar ? (
                        <div className="flex items-center gap-2">
                          <img
                            src={b.cleanerAvatar}
                            alt={b.cleanerName || ""}
                            className="w-7 h-7 rounded-full object-cover object-top"
                          />
                          <span className="text-foreground-800">{b.cleanerName}</span>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssignTarget(b);
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-accent-100 text-accent-800 hover:bg-accent-200 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-user-add-line"></i> Assign
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <StatusMenu
                        status={b.status}
                        onChange={(next) => updateStatus(b.id, next)}
                        size="sm"
                      />
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <p className="text-foreground-950 font-semibold">${b.amount}</p>
                      <span
                        className={`inline-block mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${paymentTint[b.payment]}`}
                      >
                        {b.payment === "paid"
                          ? "Paid"
                          : b.payment === "due"
                          ? "Due"
                          : "Refunded"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDrawerTarget(b);
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 cursor-pointer"
                        aria-label="Open details"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-inbox-line text-xl"></i>
              </span>
              <p className="text-sm font-semibold text-foreground-900 mt-3">
                No bookings match these filters
              </p>
              <p className="text-xs text-foreground-600 mt-1">
                Try clearing filters or adjusting your search.
              </p>
              <button
                onClick={clearFilters}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-foreground-950 text-background-50 hover:bg-foreground-800 whitespace-nowrap cursor-pointer"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Card list — mobile */}
        <ul className="md:hidden space-y-2.5">
          {filtered.map((b) => (
            <li
              key={b.id}
              onClick={() => setDrawerTarget(b)}
              className="bg-background-50 border border-background-200 rounded-2xl p-4 cursor-pointer"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <img
                    src={b.customerAvatar}
                    alt={b.customer}
                    className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-950 truncate">
                      {b.customer}
                    </p>
                    <p className="text-[11px] text-foreground-500 truncate">
                      {b.id} · {b.address}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-foreground-950">${b.amount}</p>
                  <span
                    className={`inline-block mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${paymentTint[b.payment]}`}
                  >
                    {b.payment === "paid"
                      ? "Paid"
                      : b.payment === "due"
                      ? "Due"
                      : "Refunded"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-foreground-700 mb-3 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className={`${b.serviceIcon} text-primary-600`}></i>
                  {b.service}
                </span>
                <span className="text-foreground-400">·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {b.dateLabel}, {b.time}
                </span>
              </div>

              <div className="flex items-center justify-between gap-2 pt-3 border-t border-background-200">
                {b.cleanerId && b.cleanerAvatar ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <img
                      src={b.cleanerAvatar}
                      alt={b.cleanerName || ""}
                      className="w-7 h-7 rounded-full object-cover object-top flex-shrink-0"
                    />
                    <span className="text-xs text-foreground-800 truncate">
                      {b.cleanerName}
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAssignTarget(b);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-accent-100 text-accent-800 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-user-add-line"></i> Assign cleaner
                  </button>
                )}
                <div onClick={(e) => e.stopPropagation()}>
                  <StatusMenu
                    status={b.status}
                    onChange={(next) => updateStatus(b.id, next)}
                    size="sm"
                  />
                </div>
              </div>
            </li>
          ))}

          {filtered.length === 0 && (
            <li className="bg-background-50 border border-background-200 rounded-2xl py-12 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-inbox-line text-xl"></i>
              </span>
              <p className="text-sm font-semibold text-foreground-900 mt-3">
                No bookings found
              </p>
              <button
                onClick={clearFilters}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-foreground-950 text-background-50 whitespace-nowrap cursor-pointer"
              >
                Clear filters
              </button>
            </li>
          )}
        </ul>
      </div>

      {/* Drawer */}
      <BookingDrawer
        booking={drawerTarget}
        onClose={() => setDrawerTarget(null)}
        onChangeStatus={(next) => drawerTarget && updateStatus(drawerTarget.id, next)}
        onAssignClick={() => {
          if (drawerTarget) setAssignTarget(drawerTarget);
        }}
      />

      {/* Assign modal */}
      <AssignCleanerModal
        open={!!assignTarget}
        bookingId={assignTarget?.id || null}
        currentCleanerId={assignTarget?.cleanerId || null}
        service={assignTarget?.service || ""}
        time={assignTarget?.time || ""}
        dateLabel={assignTarget?.dateLabel || ""}
        city={assignTarget?.city || ""}
        onClose={() => setAssignTarget(null)}
        onAssign={assignCleaner}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-foreground-950 text-background-50 px-4 py-2.5 rounded-full text-xs font-medium flex items-center gap-2 max-w-[calc(100vw-2rem)]">
          <i className="ri-check-line text-primary-300"></i>
          <span className="truncate">{toast}</span>
        </div>
      )}
    </AdminLayout>
  );
}